import { useEffect } from 'react';
import { Dialog as DialogPrimitive } from 'radix-ui';
import { cn } from '@/lib/utils';
import { useContributionId } from '@/store';
import { useSetSupportOpen, useSupportOpen } from '@/store/supportStore';
import { track } from '@/analytics';

const SUPPORT_EMAIL =
  import.meta.env.VITE_SUPPORT_EMAIL || 'support@miawealth.co.uk';
const SUPPORT_SUBJECT = 'Gifting help';

/**
 * The subject support searches on (GIFTWEB-32 AC 4).
 *
 * The contribution id in full, not a prettier truncation: it is the key the
 * record is stored under, so it can be pasted straight into a lookup. The
 * prototype invented a reference from the child's name, which matched nothing.
 *
 * Before the amount step there is no contribution yet — better a subject with
 * no reference than one reading "ref " with nothing after it.
 */
function buildSupportMailto(contributionId: string): string {
  const subject = contributionId
    ? `${SUPPORT_SUBJECT} - ref ${contributionId}`
    : SUPPORT_SUBJECT;
  return `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(subject)}`;
}

function QuestionIcon() {
  return (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#256956'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <circle cx='12' cy='12' r='9.2' />
      <path d='M9.4 9.2a2.7 2.7 0 0 1 5.2.9c0 1.8-2.6 2.3-2.6 3.9' />
      <path d='M12 17.4h.01' />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width='17'
      height='17'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#484747'
      strokeWidth='2.4'
      strokeLinecap='round'
      aria-hidden='true'
    >
      <path d='M18 6 6 18M6 6l12 12' />
    </svg>
  );
}

function EnvelopeIcon() {
  return (
    <svg
      width='21'
      height='21'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#256956'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
      aria-hidden='true'
    >
      <rect x='3' y='5' width='18' height='14' rx='2' />
      <path d='m3 7 9 6 9-6' />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      width='19'
      height='19'
      viewBox='0 0 24 24'
      fill='none'
      stroke='#b3bcb6'
      strokeWidth='2.4'
      strokeLinecap='round'
      strokeLinejoin='round'
      style={{ flexShrink: 0 }}
      aria-hidden='true'
    >
      <path d='M9 6l6 6-6 6' />
    </svg>
  );
}

const overlayClasses = cn(
  'fixed inset-0 z-50 bg-[rgba(16,32,25,0.42)] lg:bg-[rgba(16,32,25,0.16)]',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
);

const contentClasses = cn(
  'z-50 outline-none text-left',
  // Mobile: bottom sheet anchored to viewport
  'fixed inset-x-0 bottom-0 max-h-[85dvh] overflow-y-auto rounded-t-[26px] bg-[#fbfaf9] p-[22px] pb-safe',
  'shadow-[0_-18px_44px_-18px_rgba(20,40,32,0.55)]',
  'data-[state=open]:animate-in data-[state=closed]:animate-out',
  'data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom',
  // Desktop: floating popover, absolute inside the card, under the button
  'lg:absolute lg:inset-x-auto lg:bottom-auto lg:top-[62px] lg:right-4 lg:w-[330px]',
  'lg:rounded-2xl lg:border lg:border-[#e4e2dc] lg:p-[18px]',
  'lg:shadow-[0_22px_48px_-20px_rgba(20,40,32,0.45)]',
  'lg:data-[state=open]:slide-in-from-bottom-0 lg:data-[state=closed]:slide-out-to-bottom-0',
  'lg:data-[state=open]:fade-in-0 lg:data-[state=closed]:fade-out-0',
);

/**
 * The support pill and its sheet (GIFTWEB-32 AC 1 to AC 5).
 *
 * Open state lives in a store rather than here because the allowance sheet
 * opens this same sheet (AC 8) from inside `InfoSheet`, which is nowhere near
 * this component. The sheet itself stays here, so there is only one of it.
 */
function NeedHelp() {
  const open = useSupportOpen();
  const setOpen = useSetSupportOpen();
  const contributionId = useContributionId();
  const supportMailto = buildSupportMailto(contributionId);

  // The flag outlives this component, so leaving a route with the sheet open
  // must not reopen it on the next one.
  useEffect(() => () => setOpen(false), [setOpen]);

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        // Only reached through the trigger, Escape or the scrim — the tile and
        // the allowance sheet set the store directly and report themselves.
        if (next) track('supportOpened', { from: 'pill' });
        setOpen(next);
      }}
    >
      <DialogPrimitive.Trigger asChild>
        <button
          type='button'
          aria-label='Contact support'
          title='Contact support'
          className={cn(
            'absolute z-40 flex items-center gap-1.75 h-9.5 pl-3 pr-3.5',
            'top-4 right-4 lg:top-3.5 lg:right-4',
            'rounded-[19px] bg-white/94 border border-[#e4e2dc]',
            'shadow-[0_6px_18px_-8px_rgba(20,40,32,0.4)]',
            'cursor-pointer transition-colors hover:bg-white',
          )}
        >
          <QuestionIcon />
          <span className='text-[13px] font-bold text-[#256956] tracking-[-0.01em]'>
            Need help?
          </span>
        </button>
      </DialogPrimitive.Trigger>
      <DialogPrimitive.Overlay className={overlayClasses} />
      <DialogPrimitive.Content className={contentClasses}>
          <div
            aria-hidden='true'
            className='mx-auto -mt-2 mb-4 h-1 w-9.5 shrink-0 rounded-[3px] bg-[#e0ddd5] lg:hidden'
          />
          <div className='flex items-start gap-3'>
            <div className='flex-1 min-w-0'>
              <DialogPrimitive.Title className='text-[19px] font-bold tracking-[-0.015em] text-[#1a201d]'>
                How can we help?
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className='text-sm text-[#72706f] leading-normal mt-1'>
                Our UK team replies Mon to Fri, 9 to 5.30.
              </DialogPrimitive.Description>
            </div>
            <DialogPrimitive.Close asChild>
              <button
                type='button'
                aria-label='Close support'
                className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#ebebea] cursor-pointer'
              >
                <CloseIcon />
              </button>
            </DialogPrimitive.Close>
          </div>
          <div className='mt-4.5 flex flex-col gap-3'>
            <a
              href={supportMailto}
              onClick={() => setOpen(false)}
              className='flex items-center gap-3.5 w-full box-border text-left no-underline text-inherit bg-white border border-[#eceae3] rounded-[18px] px-3.75 py-3.5 cursor-pointer transition-shadow hover:border-[#cfd8d2] hover:shadow-[0_8px_20px_-14px_rgba(20,40,32,0.5)]'
            >
              <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-[13px] bg-[#e7f5f0]'>
                <EnvelopeIcon />
              </div>
              <div className='flex-1 min-w-0 flex flex-col items-start justify-center'>
                <div className='text-base font-bold text-[#1a201d] leading-4'>Email us</div>
                <div className='text-[13px] text-[#5a635e] mt-0.5 leading-4'>{SUPPORT_EMAIL}</div>
              </div>
              <ChevronIcon />
            </a>
            <div className='text-xs text-[#8a938c] leading-[1.45]'>
              Opens your email app with your reference filled in.
            </div>
          </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
}

export default NeedHelp;
